import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: 'BAG',
    price: '',
    condition: 'Very Good',
    description: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  useEffect(() => {
    checkAuth();
    loadProducts();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleExportGoogleMerchant = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products/export/google-merchant`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `google_merchant_${new Date().toISOString().split('T')[0]}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export fehlgeschlagen. Bitte versuchen Sie es erneut.');
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 10); // Max 10 images
    setSelectedImages(files);
    
    // Create previews
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreview(previews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    const token = localStorage.getItem('admin_token');
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('condition', formData.condition);
      formDataToSend.append('description', formData.description);
      
      if (editingProduct) {
        // Update existing product (without images, those are managed separately)
        await axios.put(
          `${BACKEND_URL}/api/products/${editingProduct.id}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        setFormSuccess('Product updated successfully!');
      } else {
        // Create new product with images
        selectedImages.forEach(image => {
          formDataToSend.append('images', image);
        });
        
        await axios.post(
          `${BACKEND_URL}/api/products`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        setFormSuccess('Product created successfully!');
      }

      // Reset form
      setFormData({
        title: '',
        category: 'BAG',
        price: '',
        condition: 'Very Good',
        description: ''
      });
      setSelectedImages([]);
      setImagePreview([]);
      setEditingProduct(null);
      setShowForm(false);
      
      // Reload products
      loadProducts();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      category: product.category,
      price: product.price,
      condition: product.condition,
      description: product.description || ''
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.delete(`${BACKEND_URL}/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      loadProducts();
    } catch (err) {
      alert('Failed to delete product');
    }
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({
      title: '',
      category: 'BAG',
      price: '',
      condition: 'Very Good',
      description: ''
    });
    setSelectedImages([]);
    setImagePreview([]);
    setFormError('');
    setFormSuccess('');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--soft-gray)' }}>
      {/* Header */}
      <div 
        className="border-b"
        style={{ backgroundColor: 'white', borderColor: '#e5e7eb' }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 
            className="text-2xl font-light"
            style={{ fontFamily: 'Playfair Display, serif', color: 'var(--charcoal)' }}
          >
            Admin Dashboard
          </h1>
          <div className="flex gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
            >
              View Site
            </Button>
            <Button
              onClick={handleLogout}
              style={{ backgroundColor: 'var(--warm-sage)', color: 'white' }}
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Action Buttons */}
        {!showForm && (
          <div className="mb-8 flex flex-wrap gap-4">
            <Button
              onClick={() => setShowForm(true)}
              style={{ backgroundColor: 'var(--warm-sage)', color: 'white' }}
            >
              + Add New Product
            </Button>
            <Button
              onClick={handleExportGoogleMerchant}
              variant="outline"
              className="flex items-center gap-2"
              style={{ borderColor: 'var(--lv-brown)', color: 'var(--lv-brown)' }}
            >
              <Download className="w-4 h-4" />
              Google Merchant XML
            </Button>
          </div>
        )}

        {/* Product Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    placeholder="e.g. Louis Vuitton Neverfull"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <select
                      className="w-full border rounded-md px-3 py-2"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      <option value="BAG">BAG</option>
                      <option value="SHOES">SHOES</option>
                      <option value="ACCESSORY">ACCESSORY</option>
                      <option value="CLOTHING">CLOTHING</option>
                      <option value="WALLET">WALLET</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Price (CHF)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      required
                      placeholder="299.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Condition</label>
                    <select
                      className="w-full border rounded-md px-3 py-2"
                      value={formData.condition}
                      onChange={(e) => setFormData({...formData, condition: e.target.value})}
                    >
                      <option value="Excellent">Excellent</option>
                      <option value="Very Good">Very Good</option>
                      <option value="Good">Good</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    placeholder="Additional product details..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Bilder verwalten (max 10)
                  </label>
                  
                  {/* Show existing images when editing */}
                  {editingProduct && (
                    <div className="mb-4 p-4 bg-gray-50 rounded">
                      <div className="flex justify-between items-center mb-3">
                        <p className="text-sm font-medium">
                          Aktuelle Bilder ({editingProduct.images?.length || 0}/10)
                        </p>
                        {editingProduct.images && editingProduct.images.length < 10 && (
                          <>
                            <input
                              id={`upload-images-${editingProduct.id}`}
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={async (e) => {
                                const files = Array.from(e.target.files);
                                if (files.length === 0) return;
                                
                                const token = localStorage.getItem('admin_token');
                                const formData = new FormData();
                                files.forEach(file => {
                                  formData.append('images', file);
                                });
                                
                                try {
                                  const response = await axios.post(
                                    `${BACKEND_URL}/api/products/${editingProduct.id}/images`,
                                    formData,
                                    { 
                                      headers: { 
                                        Authorization: `Bearer ${token}`
                                      } 
                                    }
                                  );
                                  
                                  alert(response.data.message || 'Bilder hochgeladen!');
                                  loadProducts();
                                  
                                  // Reload editing product
                                  const updated = await axios.get(`${BACKEND_URL}/api/products/${editingProduct.id}`);
                                  setEditingProduct(updated.data);
                                  
                                  // Reset file input
                                  e.target.value = '';
                                } catch (err) {
                                  console.error('Upload error:', err);
                                  alert('Fehler beim Hochladen: ' + (err.response?.data?.detail || err.message));
                                }
                              }}
                            />
                            <Button 
                              type="button" 
                              size="sm" 
                              style={{ backgroundColor: 'var(--warm-sage)', color: 'white' }}
                              onClick={() => {
                                document.getElementById(`upload-images-${editingProduct.id}`).click();
                              }}
                            >
                              + Bild(er) hinzufügen
                            </Button>
                          </>
                        )}
                      </div>
                      
                      {editingProduct.images && editingProduct.images.length > 0 ? (
                        <div className="grid grid-cols-5 gap-3">
                          {editingProduct.images.map((imgId, idx) => (
                            <div key={imgId} className="relative group">
                              <img
                                src={`${BACKEND_URL}/api/uploads/products/${imgId}`}
                                alt={`Image ${idx + 1}`}
                                className="w-full h-24 object-cover rounded border-2 border-gray-300"
                              />
                              <span className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                                {idx + 1}
                              </span>
                              
                              {/* Action buttons */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                                {/* Rotate button */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const token = localStorage.getItem('admin_token');
                                    try {
                                      await axios.post(
                                        `${BACKEND_URL}/api/products/${editingProduct.id}/images/${imgId}/rotate`,
                                        {},
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      loadProducts();
                                      const updated = await axios.get(`${BACKEND_URL}/api/products/${editingProduct.id}`);
                                      setEditingProduct(updated.data);
                                    } catch (err) {
                                      alert('Fehler beim Rotieren');
                                    }
                                  }}
                                  className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  title="90° drehen"
                                >
                                  🔄
                                </button>
                                
                                {/* Delete button */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!confirm('Bild löschen?')) return;
                                    
                                    const token = localStorage.getItem('admin_token');
                                    try {
                                      await axios.delete(
                                        `${BACKEND_URL}/api/products/${editingProduct.id}/images/${imgId}`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      loadProducts();
                                      const updated = await axios.get(`${BACKEND_URL}/api/products/${editingProduct.id}`);
                                      setEditingProduct(updated.data);
                                    } catch (err) {
                                      alert('Fehler beim Löschen');
                                    }
                                  }}
                                  className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                  title="Löschen"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-center py-4" style={{ color: 'var(--warm-gray)' }}>
                          Noch keine Bilder hochgeladen
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* For new products, show upload field */}
                  {!editingProduct && (
                    <>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        required={true}
                      />
                      {imagePreview.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium mb-2">Vorschau ({imagePreview.length}):</p>
                          <div className="grid grid-cols-5 gap-2">
                            {imagePreview.map((preview, idx) => (
                              <img
                                key={idx}
                                src={preview}
                                alt={`Preview ${idx + 1}`}
                                className="w-full h-24 object-cover rounded border-2"
                                style={{ borderColor: 'var(--warm-sage)' }}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {formError && (
                  <div className="p-3 rounded" style={{ backgroundColor: '#fee', color: '#c33' }}>
                    {formError}
                  </div>
                )}

                {formSuccess && (
                  <div className="p-3 rounded" style={{ backgroundColor: '#efe', color: '#363' }}>
                    {formSuccess}
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    style={{ backgroundColor: 'var(--warm-sage)', color: 'white' }}
                  >
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Products List */}
        <Card>
          <CardHeader>
            <CardTitle>All Products ({products.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading products...</p>
            ) : products.length === 0 ? (
              <p className="text-center py-8" style={{ color: 'var(--warm-gray)' }}>
                No products yet. Add your first product!
              </p>
            ) : (
              <div className="space-y-4">
                {products.map(product => (
                  <div
                    key={product.id}
                    className="flex gap-4 p-4 border rounded-lg"
                    style={{ backgroundColor: 'white' }}
                  >
                    <div className="flex-shrink-0">
                      {product.images && product.images.length > 0 ? (
                        <div className="space-y-2">
                          {/* Main/First Image */}
                          <img
                            src={`${BACKEND_URL}/api/uploads/products/${product.images[0]}`}
                            alt={product.title}
                            className="w-32 h-32 object-cover rounded"
                          />
                          {/* Image Count Badge */}
                          {product.images.length > 1 && (
                            <div className="text-center">
                              <span className="text-xs px-2 py-1 rounded bg-gray-100" style={{ color: 'var(--warm-gray)' }}>
                                +{product.images.length - 1} mehr
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 rounded flex items-center justify-center">
                          <span className="text-xs" style={{ color: 'var(--warm-gray)' }}>Keine Bilder</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-lg">{product.title}</h3>
                      <p className="text-sm" style={{ color: 'var(--warm-gray)' }}>
                        {product.category} • {product.condition} • CHF {product.price}
                      </p>
                      {product.description && (
                        <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--warm-gray)' }}>
                          {product.description}
                        </p>
                      )}
                      {/* Show all images count */}
                      {product.images && product.images.length > 0 && (
                        <p className="text-xs mt-2" style={{ color: 'var(--warm-sage)' }}>
                          📸 {product.images.length} Bild{product.images.length !== 1 ? 'er' : ''} hochgeladen
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEdit(product)}
                        variant="outline"
                        size="sm"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(product.id)}
                        variant="outline"
                        size="sm"
                        style={{ color: '#c33', borderColor: '#c33' }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminDashboard;
